/**
 * Integration API client functions
 *
 * Handles fetching integration catalog, user integrations,
 * and OAuth flows for connecting integrations.
 */

import { authClient } from "./auth-client";
import { apiClient } from "./client";

/**
 * Customer information with enriched details
 */
export interface CustomerInfo {
	id: string;
	descriptive_name: string;
	manager: boolean;
	status: "ENABLED" | "CANCELED" | "SUSPENDED" | "CLOSED" | "UNKNOWN" | "UNSPECIFIED";
}

/**
 * Sensitive keys that should be removed from meta_json
 */
const SENSITIVE_KEYS = new Set([
	"client_secret",
	"refresh_token",
	"access_token",
	"api_key",
	"private_key",
	"secret",
	"password",
]);

/**
 * Recursively remove sensitive keys from an object
 */
function sanitizeMetadata(
	obj: { [key: string]: unknown } | null | undefined
): { [key: string]: unknown } | undefined {
	if (obj === null || obj === undefined) {
		return undefined;
	}
	const sanitized: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(obj)) {
		if (!SENSITIVE_KEYS.has(key)) {
			if (value && typeof value === "object" && !Array.isArray(value)) {
				sanitized[key] = sanitizeMetadata(value as { [key: string]: unknown });
			} else if (Array.isArray(value)) {
				sanitized[key] = value.map((item) =>
					item && typeof item === "object" && !Array.isArray(item)
						? sanitizeMetadata(item as { [key: string]: unknown })
						: item
				);
			} else {
				sanitized[key] = value;
			}
		}
	}
	return sanitized;
}

/**
 * Get catalog of all available app integrations (public endpoint)
 */
export async function getAppIntegrations() {
	const response = await apiClient.GET("/api/v1/integrations/catalog");
	if (response.error) {
		throw new Error("Failed to fetch app integrations catalog");
	}
	return response.data;
}

/**
 * Get user's connected integrations
 * Sanitizes sensitive metadata fields before returning
 */
export async function getUserIntegrations() {
	const response = await authClient.GET("/api/v1/integrations/");
	if (response.error) {
		throw new Error("Failed to fetch user integrations");
	}
	// Sanitize meta_json to remove sensitive fields
	if (Array.isArray(response.data)) {
		return response.data.map((integration) => ({
			...integration,
			meta_json: integration.meta_json
				? sanitizeMetadata(integration.meta_json)
				: integration.meta_json,
		}));
	}
	return response.data;
}

/**
 * Initialize Google Ads OAuth flow
 */
export async function initGoogleAdsOAuth(redirectUri: string) {
	const response = await authClient.POST("/api/v1/integrations/google-ads/auth/init", {
		body: {
			redirect_uri: redirectUri,
			access_type: "offline",
		},
	});
	if (response.error) {
		throw new Error("Failed to initialize Google Ads OAuth");
	}
	return response.data;
}

/**
 * Complete Google Ads OAuth flow
 */
export async function completeGoogleAdsOAuth(code: string, state: string, redirectUri: string) {
	const response = await authClient.POST("/api/v1/integrations/google-ads/auth/callback", {
		body: {
			code,
			state,
			redirect_uri: redirectUri,
		},
	});
	if (response.error) {
		throw new Error("Failed to complete Google Ads OAuth");
	}
	return response.data;
}

/**
 * Complete Google Search Console OAuth flow
 */
export async function completeGoogleSearchConsoleOAuth(
	code: string,
	state: string,
	redirectUri: string
) {
	const response = await authClient.POST(
		"/api/v1/integrations/google-search-console/auth/callback",
		{
			body: {
				code,
				state,
				redirect_uri: redirectUri,
			},
		}
	);
	if (response.error) {
		throw new Error("Failed to complete Google Search Console OAuth");
	}
	return response.data;
}

/**
 * Get Google Ads integration status
 */
export async function getGoogleAdsStatus() {
	const response = await authClient.GET("/api/v1/integrations/google-ads/status");
	if (response.error) {
		throw new Error("Failed to fetch Google Ads status");
	}
	return response.data;
}

/**
 * Delete Google Ads integration
 */
export async function deleteGoogleAdsIntegration() {
	const response = await authClient.DELETE("/api/v1/integrations/google-ads");
	if (response.error) {
		throw new Error("Failed to delete Google Ads integration");
	}
	return response.data;
}

/**
 * Delete Google Search Console integration
 */
export async function deleteGoogleSearchConsoleIntegration() {
	const response = await authClient.DELETE("/api/v1/integrations/google-search-console");
	if (response.error) {
		throw new Error("Failed to delete Google Search Console integration");
	}
	return response.data;
}

/**
 * List accessible Google Ads customers
 */
export async function listAccessibleCustomers(
	refresh = false
): Promise<{ accessible_customers: CustomerInfo[]; refreshed: boolean }> {
	const response = await authClient.GET("/api/v1/integrations/google-ads/customers", {
		params: {
			query: {
				refresh: refresh,
			},
		},
	});
	if (response.error) {
		throw new Error("Failed to fetch accessible customers");
	}
	return response.data as unknown as {
		accessible_customers: CustomerInfo[];
		refreshed: boolean;
	};
}

/**
 * Set Google Ads customer ID
 */
export async function setGoogleAdsCustomerId(
	customerId: string
): Promise<{ success: boolean; customer_id: string; message: string }> {
	const response = await authClient.PUT("/api/v1/integrations/google-ads/customer-id", {
		body: {
			customer_id: customerId,
		},
	});
	if (response.error) {
		throw new Error("Failed to set customer ID");
	}
	return response.data as unknown as {
		success: boolean;
		customer_id: string;
		message: string;
	};
}

/**
 * Connect integration with API key (Stripe, GitHub, etc.)
 */
export async function connectWithApiKey(
	appIntegrationId: number,
	apiKey: string,
	options?: Record<string, string>
): Promise<{
	success: boolean;
	integration_id: number;
	message: string;
}> {
	const response = await authClient.POST("/api/v1/integrations/{app_integration_id}/connect", {
		params: { path: { app_integration_id: appIntegrationId } },
		body: { api_key: apiKey, options: (options ?? {}) as Record<string, unknown> },
	});
	if (response.error) {
		throw new Error(`Failed to connect integration ${appIntegrationId}`);
	}
	return response.data as unknown as {
		success: boolean;
		integration_id: number;
		message: string;
	};
}

/**
 * Disconnect any integration (works for both OAuth and API key)
 */
export async function disconnectIntegration(
	appIntegrationId: number
): Promise<{ success: boolean; message: string }> {
	const response = await authClient.DELETE("/api/v1/integrations/{app_integration_id}", {
		params: { path: { app_integration_id: appIntegrationId } },
	});
	if (response.error) {
		throw new Error(`Failed to disconnect integration ${appIntegrationId}`);
	}
	return response.data as unknown as {
		success: boolean;
		message: string;
	};
}

/**
 * Error returned when Composio requires additional fields (e.g. subdomain for Confluence)
 */
export interface ComposioRequiredFieldsError {
	status: 422;
	required_fields: { name: string; displayName: string; description: string }[];
}

/**
 * Initialize Composio OAuth flow
 */
export async function initComposioOAuth(
	appIntegrationId: number,
	redirectUri: string,
	connectionData?: Record<string, string>
): Promise<{ redirect_url: string; connection_id: string }> {
	const response = await authClient.POST(
		"/api/v1/integrations/{app_integration_id}/composio/init",
		{
			params: { path: { app_integration_id: appIntegrationId } },
			body: { redirect_uri: redirectUri, connection_data: connectionData } as never,
		}
	);
	if (response.error) {
		const detail = (response.error as { detail?: { required_fields?: unknown[] } }).detail;
		if (response.response.status === 422 && detail?.required_fields) {
			throw { status: 422, required_fields: detail.required_fields } as ComposioRequiredFieldsError;
		}
		throw new Error("Failed to initialize Composio OAuth");
	}
	return response.data as unknown as { redirect_url: string; connection_id: string };
}

/**
 * Complete Composio OAuth callback
 */
export async function completeComposioCallback(
	appIntegrationId: number,
	connectionId: string
): Promise<{ status: string; integration_id: number }> {
	const response = await authClient.POST(
		"/api/v1/integrations/{app_integration_id}/composio/callback",
		{
			params: { path: { app_integration_id: appIntegrationId } },
			body: { connection_id: connectionId },
		}
	);
	if (response.error) {
		throw new Error("Failed to complete Composio OAuth");
	}
	return response.data as unknown as { status: string; integration_id: number };
}

/**
 * Get MCP configuration for Claude Desktop and other MCP clients
 */
export async function getMcpConfig(): Promise<{
	note: string;
	api_key_prefix: string | null;
	connected_integrations: string[];
	mcpServers: {
		[key: string]: {
			type: string;
			url: string;
			headers: { Authorization: string };
			description?: string;
		};
	};
}> {
	const response = await authClient.GET("/api/v1/mcp/config", {});
	if (response.error) {
		throw new Error("Failed to fetch MCP configuration");
	}
	return response.data as unknown as {
		note: string;
		api_key_prefix: string | null;
		connected_integrations: string[];
		mcpServers: {
			[key: string]: {
				type: string;
				url: string;
				headers: { Authorization: string };
				description?: string;
			};
		};
	};
}
